
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { Debounce, cancelDebounce } from '@/utils/decorators';
import { addBorderAroundTable, addStylingToHorizontalHeader, downloadFile, setCellFontSizeAndAlignement, alignColumns } from '@/utils/exceljs-helpers';
import * as ExcelJS from 'exceljs';
import Toast from '@/utils/toast';
import WarningModal from '@/components/shared/suites/WarningModal.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import TableHeaders from '@/components/shared/suites/TableHeaders.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import PaginationControls from '@/components/shared/suites/PaginationControls.vue';
import CreditRowItem from '@/components/credits/CreditRowItem.vue';
import CreditsChangeHistoryModal from '@/components/credits/CreditsChangeHistoryModal.vue';
import CreditModal from '@/components/credits/CreditModal.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'auth';

@Component<CreditsSettings>({
	components: {
		TextField,
		SuitesHeader,
		TableHeaders,
		TextButton,
		PaginationControls,
		CreditRowItem,
		CreditsChangeHistoryModal,
		CreditModal,
		WarningModal
	}
})
export default class CreditsSettings extends Vue {
	@Action('getCredits', { namespace }) private getApp8Credits!: (filterParams?: TableFilterParams) => Promise<PaginatedResults<App8Credit>>;
	@Getter('isK12Location', { namespace }) private isK12Location!: boolean;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;

	private isLoading: boolean = false;
	private showCreditModal: boolean = false;
	private showChangeHistoryModal: boolean = false;
	private showK12WarningModal: boolean = false;
	private selectedCredit: App8Credit | null = null;
	private credits: App8Credit[] = [];
	private totalCount: number = 0;
	private currentPage: number = 1;
	private itemsPerPage: number = 10;
	private searchInput: string = '';
	private sortBy: string = 'expiry';
	private sortDirection: 'asc' | 'desc' = 'desc';

	private get tableHeaders(): TableHeader[] {
		return [
			{ text: this.$t('settings.credits.table.name') },
			{ text: this.$t('settings.credits.table.email') },
			{ text: this.$t('settings.credits.table.expiry'), name: 'expiry', sort: this.sortBy === 'expiry' ? this.sortDirection : 'desc' },
			{ text: this.$t('settings.credits.table.balance'), name: 'balance', sort: this.sortBy === 'balance' ? this.sortDirection : 'desc' },
			{ text: this.$t('settings.credits.table.edit') }
		];
	}

	/**
	 * Get the total number of pages for pagination
	 *
	 * @return {number}
	 */
	private get totalPages(): number {
		return Math.ceil(this.totalCount / this.itemsPerPage);
	}

	private get isK12AndMisconfigured(): boolean {
		const timezone = this.restaurant.menu_configuration?.zone || 'America/Toronto';
		return this.isK12Location
			&& !!this.restaurant.menu_manager_config
			&& (!this.restaurant.menu_manager_config.end_school_year_date
				|| DateTime.now().setZone(timezone).endOf('day') > DateTime.fromISO(this.restaurant.menu_manager_config.end_school_year_date, { zone: timezone }).endOf('day'));
	}

	private created(): void {
		this.getCredits();
	}

	private beforeDestroy(): void {
		cancelDebounce(this.debouncedSearch);
	}

	/**
	 * Fetch the credits with server-side filtering, sorting, and pagination
	 *
	 * @return {Promise<void>}
	 */
	private async getCredits(): Promise<void> {
		this.isLoading = true;
		try {
			const filterParams: TableFilterParams = {
				page: this.currentPage,
				limit: this.itemsPerPage,
				search: this.searchInput || undefined,
				sortBy: this.sortBy,
				sortDirection: this.sortDirection
			};

			const response = await this.getApp8Credits(filterParams);

			// Process paginated response
			this.credits = response.rows.map((credit: App8Credit) => {
				if (credit.expiry) {
					credit.expiry = DateTime.fromISO(credit.expiry);
				}
				return credit;
			});
			this.totalCount = response.count;

			this.isLoading = false;
		}
		catch (errorMessage) {
			Toast.error(this.$t('settings.credits.error_fetching', { errorMessage }));
			this.isLoading = false;
		}
	}

	/**
	 * Handle column sorting changes (similar to OrderManagement pattern)
	 *
	 * @param {string} columnName - The name of the column to sort
	 * @return {Promise<void>}
	 */
	private async updateSorting(columnName: string): Promise<void> {
		// Only allow sorting on expiry and balance columns
		if (columnName !== 'expiry' && columnName !== 'balance') {
			return;
		}

		let newDirection: 'asc' | 'desc';

		if (this.sortBy === columnName) {
			// Toggle direction if same column
			newDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		}
		else {
			// Set default direction for new column
			newDirection = 'desc'; // Default to desc for both expiry and balance
		}

		this.sortBy = columnName;
		this.sortDirection = newDirection;
		this.currentPage = 1; // Reset to first page when sorting changes

		// Fetch new data with updated sort
		await this.getCredits();
	}

	/**
	 * Open the credit modal for the selected credit. If the location is K12 and misconfigured, show a warning modal instead.
	 *
	 * @param {App8Credit} credit - The credit to edit
	 * @return {void}
	 */
	private openEditModal(credit: App8Credit): void {
		if (this.isK12AndMisconfigured) {
			this.showK12WarningModal = true;
			return;
		}
		this.selectedCredit = credit;
		this.showCreditModal = true;
	}

	/**
	 * Export the credits to an Excel file.
	 *
	 * @return {Promise<void>}
	 */
	private async exportCredits(): Promise<void> {
		const workbook = new ExcelJS.Workbook();

		const worksheet = workbook.addWorksheet(this.$t('settings.credits.export.title'), { pageSetup: { orientation: 'portrait', fitToPage: true } });

		worksheet.columns = [
			{ header: this.$t('settings.credits.table.name'), key: 'name', width: 40 },
			{ header: this.$t('settings.credits.table.email'), key: 'email', width: 40 },
			{ header: this.$t('settings.credits.table.expiry'), key: 'expiry', width: 20 },
			{ header: this.$t('settings.credits.table.balance'), key: 'balance', width: 25 }
		];

		// Get all credits for export (without pagination)
		const exportFilterParams: TableFilterParams = {
			search: this.searchInput || undefined,
			sortBy: this.sortBy,
			sortDirection: this.sortDirection
			// No page/limit to get all data
		};

		const exportResponse = await this.getApp8Credits(exportFilterParams);

		const creditsData = exportResponse.rows.map((credit) => {
			return {
				expiry: credit.expiry ? DateTime.fromISO(credit.expiry).setZone('UTC', { keepLocalTime: true }).toFormat(this.$t('settings.credits.table.date_format')) : '',
				email: credit.email,
				name: credit.name,
				balance: `${this.$n(credit.balance, 'currency')}${credit.expiry && credit.expiry <= DateTime.now() ? this.$t('settings.credits.table.expired') : ''}`
			};
		});

		worksheet.addRows(creditsData);

		setCellFontSizeAndAlignement(worksheet, 14, { vertical: 'top', horizontal: 'left' });
		addStylingToHorizontalHeader(worksheet);
		alignColumns(worksheet, [4], 'right');
		addBorderAroundTable(worksheet, 1, worksheet.rowCount);

		// Save the workbook to a file
		workbook.xlsx.writeBuffer().then(buffer => {
			downloadFile(buffer, this.$t('settings.credits.export.file_name', { date: DateTime.now().toFormat(this.$t('settings.credits.export.date_format')) }));
		});
	}

	/**
	 * Open the credit modal. If the location is K12 and misconfigured, show a warning modal instead.
	 *
	 * @return {void}
	 */
	private openCreditModal(): void {
		if (this.isK12AndMisconfigured) {
			this.showK12WarningModal = true;
			return;
		}
		this.showCreditModal = true;
	}

	private navigateToBusinessSettings(): void {
		this.showK12WarningModal = false;
		this.$emit('navigate-to', 'business-settings');
	}

	/**
	 * Navigate to the previous page if available
	 *
	 * @return {Promise<void>}
	 */
	private async goToPreviousPage(): Promise<void> {
		if (this.currentPage > 1) {
			this.currentPage--;
			await this.getCredits();
		}
	}

	/**
	 * Navigate to the next page if available
	 *
	 * @return {Promise<void>}
	 */
	private async goToNextPage(): Promise<void> {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			await this.getCredits();
		}
	}

	/**
	 * Debounced search method to fetch credits with search term
	 *
	 * @return {Promise<void>}
	 */
	@Debounce(500)
	private async debouncedSearch(): Promise<void> {
		this.currentPage = 1; // Reset to first page when searching
		await this.getCredits();
	}

	/**
	 * Clear the search input and reset pagination
	 *
	 * @return {Promise<void>}
	 */
	private async clearSearch(): Promise<void> {
		this.searchInput = '';
		this.currentPage = 1;
		await this.getCredits();
	}
}
